import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import Fade from '@rio-cloud/rio-uikit/Fade';
import Notification from '@rio-cloud/rio-uikit/Notification';
import throttle from 'lodash/throttle';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { type IntlShape, useIntl } from 'react-intl';

import {
    useDeleteSubscriptionMutation,
    useGetSubscriptionsQuery,
    usePostSubscriptionMutation,
} from '#api-bridge/notificationsApi.ts';
import { useCreateTourMutation, useUpdateTourMutation } from '#api-bridge/tourApi.ts';
import { EditStopDialog } from '#components/tour/EditStopDialog/EditStopDialog.tsx';
import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import { isStopEditable } from '#domain/isStopEditable.ts';
import type { OrderSummary } from '#domain/Order.ts';
import { isServiceOrder } from '#domain/Order.ts';
import type { TourDetails } from '#domain/Tour.ts';
import { useAppNavigate } from '#routes/AppNavLink.tsx';
import { routes } from '#routes/routes.ts';

import { ValidationViolation } from '../../form/ValidationViolation.tsx';
import { createTourSubscriptions } from '../createTourSubscriptions.ts';
import { CapacityExceededConfirmationDialog } from './CapacityExceededConfirmationDialog.tsx';
import { DialogFooter } from './DialogFooter.tsx';
import { type CapacityLimits, getTourLoadMetrics, hasExceededTourCapacity } from './loadMetrics.ts';
import { ToBackendDTO, type TourFormValues } from './NewTourFormState.ts';
import { OpenOrdersList } from './OpenOrdersList.tsx';
import { sortOrderOptions } from './orderOptions.ts';
import { SortableTourList } from './SortableTourList.tsx';
import { TourCapacityForm } from './TourCapacityForm.tsx';
import { TourForm } from './TourForm.tsx';
import { TourFormValidation } from './TourFormValidation.ts';
import { canRemoveStopFromTour, getStopsFromTour, mapStopTypeToStop, type TourStop } from './TourStop.ts';
import { TourStopsEmptyState } from './TourStopsEmptyState.tsx';
import { TourStopsMap } from './TourStopsMap.tsx';
import { toCapacityWeightKg } from './tourCapacity.ts';
import { useGetDriverOptions } from './useGetDriverOptions.ts';
import { useGetVehicleOptions } from './useGetVehicleOptions.ts';
import { useHandleTourStops } from './useHandleTourStops.tsx';
import { VehicleAvailabilityTimeline } from './VehicleAvailabilityTimeline.tsx';
import { validateEditedStopOrder } from './validateEditedStopOrder.ts';

export type DialogProps = {
    close: () => void;
    isOpen: boolean;
    orders: OrderSummary[];
    availableOrders: OrderSummary[];
    isLoadingOrders?: boolean;
    isOrderError?: boolean;
    onRetryOrders?: () => void;
    tourToEdit?: TourDetails;
};

const createDefaultValues = (selectedOrders: OrderSummary[], tourToEdit?: TourDetails): TourFormValues => ({
    tourNumber: tourToEdit?.searchName ?? '',
    driver: tourToEdit?.driverId ?? '',
    vehicle: tourToEdit?.truck.id ?? '',
    selectedOrderIds: selectedOrders.map(order => order.id),
    maxWeightT: tourToEdit?.capacity?.maxWeightKg === undefined ? undefined : tourToEdit.capacity.maxWeightKg / 1000,
    maxLoadingMeter: tourToEdit?.capacity?.maxLoadingMeter,
    maxVolumeLiter: tourToEdit?.capacity?.maxVolumeLiter,
    maxVolumeCubicMeter: tourToEdit?.capacity?.maxVolumeCubicMeter,
});

const showOrderCanNotBeRemovedFormTourNotification = (intl: IntlShape) =>
    Notification.error(
        intl.formatMessage({
            id: 'intl-msg:ordercom-cloud-frontend.tours.edit.remove-unload-after-finished-load.error',
        })
    );

export const CreateTourDialog = memo(
    ({
        close,
        isOpen,
        orders,
        availableOrders,
        tourToEdit,
        isLoadingOrders,
        isOrderError,
        onRetryOrders,
    }: DialogProps) => {
        const intl = useIntl();
        const navigate = useAppNavigate();
        const [notificationsEnabled, setNotificationsEnabled] = useState(true);
        const [stopToEdit, setStopToEdit] = useState<TourStop>();
        const [pendingTourPayload, setPendingTourPayload] = useState<TourFormValues>();
        const showEditStopButton = useFeatureToggle('showEditStopButton', false);
        const useNewTourDialog = useFeatureToggle('showNewCreateTourDialog', false);
        const showLoadMetrics = useFeatureToggle('showLoadMetrics', false);

        const driverOptions = useGetDriverOptions();
        const vehicleOptions = useGetVehicleOptions({ skip: useNewTourDialog || !isOpen });

        const [createTour, { isLoading: isLoadingCreateTour }] = useCreateTourMutation();
        const [updateTour, { isLoading: isLoadingUpdateTour }] = useUpdateTourMutation();
        const [createSubscription, { isLoading: isLoadingPostSubscription }] = usePostSubscriptionMutation();
        const [deleteSubscription, { isLoading: isLoadingDeleteSubscription }] = useDeleteSubscriptionMutation();
        const isEditMode = Boolean(tourToEdit);
        const { data: existingSubscriptions, isLoading: isLoadingGetSubscriptions } = useGetSubscriptionsQuery(
            { tourId: tourToEdit?.id },
            { skip: !isOpen || !tourToEdit?.id }
        );
        const isLoading =
            isLoadingCreateTour ||
            isLoadingUpdateTour ||
            isLoadingPostSubscription ||
            isLoadingDeleteSubscription ||
            isLoadingGetSubscriptions;
        const dialogTitle = isEditMode
            ? intl.formatMessage({
                  id: 'intl-msg:ordercom-cloud-frontend.tours.edit-dialog.title',
                  defaultMessage: 'Edit tour',
              })
            : intl.formatMessage({
                  id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.title',
                  defaultMessage: 'Create tour',
              });
        const dialogTitleId = isEditMode
            ? 'intl-msg:ordercom-cloud-frontend.tours.edit-dialog.title'
            : 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.title';
        const submitLabel = isEditMode
            ? intl.formatMessage({
                  id: 'intl-msg:ordercom-cloud-frontend.tours.edit-dialog.save',
                  defaultMessage: 'Save tour',
              })
            : dialogTitle;
        const submitTrackingKey = isEditMode
            ? 'intl-msg:ordercom-cloud-frontend.tours.edit-dialog.save'
            : dialogTitleId;

        const {
            control,
            formState: { errors, isDirty },
            handleSubmit,
            reset,
            setValue,
            watch,
        } = useForm<TourFormValues>({
            defaultValues: createDefaultValues(orders, tourToEdit),
            resolver: zodResolver(TourFormValidation),
        });

        const selectableOrders = availableOrders;
        const selectedOrderIds = watch('selectedOrderIds');
        const maxWeightT = watch('maxWeightT');
        const maxLoadingMeter = watch('maxLoadingMeter');
        const maxVolumeLiter = watch('maxVolumeLiter');
        const maxVolumeCubicMeter = watch('maxVolumeCubicMeter');
        const capacity = {
            maxWeightKg: toCapacityWeightKg(maxWeightT),
            maxLoadingMeter,
            maxVolumeLiter,
            maxVolumeCubicMeter,
        };
        const capacityLimits: CapacityLimits = {
            weight: capacity.maxWeightKg,
            loadingMeter: capacity.maxLoadingMeter,
            liter: capacity.maxVolumeLiter,
            cubicMeter: capacity.maxVolumeCubicMeter,
        };
        const selectedVehicleId = watch('vehicle');
        const selectedOrderIdsKey = selectedOrderIds.join('|');
        const selectedOrders = useMemo(() => {
            const orderById = new Map(selectableOrders.map(order => [order.id, order]));
            return selectedOrderIds.flatMap(orderId => {
                const order = orderById.get(orderId);
                return order ? [order] : [];
            });
        }, [selectableOrders, selectedOrderIds]);
        const orderOptions = useMemo(
            () => sortOrderOptions(selectableOrders, selectedOrderIds),
            [selectableOrders, selectedOrderIds]
        );
        const orderByStopId = useMemo(() => {
            const ordersByStopId = new Map<string, OrderSummary>();
            selectedOrders.forEach(order => {
                if (isServiceOrder(order)) {
                    ordersByStopId.set(order.executionStop.id, order);
                } else {
                    ordersByStopId.set(order.loadingStop.id, order);
                    ordersByStopId.set(order.unloadingStop.id, order);
                }
            });
            return ordersByStopId;
        }, [selectedOrders]);

        const initialStops = useMemo(() => {
            if (!tourToEdit) {
                return undefined;
            }
            return getStopsFromTour(tourToEdit, selectedOrders);
        }, [selectedOrderIdsKey, tourToEdit?.id]);

        const selectedOrderById = useMemo(
            () => new Map(selectedOrders.map(order => [order.id, order])),
            [selectedOrders]
        );

        const { sortableTourStops, setSortableTourStops, isMisplaced, misplacedStopIds } = useHandleTourStops(
            isOpen,
            selectedOrders,
            initialStops
        );
        const exceededCapacityDimensions =
            showLoadMetrics && useNewTourDialog
                ? [
                      ...new Set(
                          [...getTourLoadMetrics(sortableTourStops, selectedOrders, capacityLimits).values()].flatMap(
                              metrics => metrics.exceededCapacity ?? []
                          )
                      ),
                  ]
                : [];
        const isTourStarted =
            tourToEdit?.status === 'in-transit' ||
            tourToEdit?.status === 'finished' ||
            tourToEdit?.status === 'problem';
        const editableStopIds = useMemo(() => {
            if (!showEditStopButton) {
                return new Set<string>();
            }

            return new Set(
                sortableTourStops
                    .filter(stop =>
                        isStopEditable(
                            mapStopTypeToStop(stop),
                            orderByStopId.get(stop.stopId ?? stop.id),
                            tourToEdit?.status
                        )
                    )
                    .map(stop => stop.stopId ?? stop.id)
            );
        }, [orderByStopId, showEditStopButton, sortableTourStops, tourToEdit?.status]);

        const updateStopInTourDialog = (editedStop: TourStop) => {
            setSortableTourStops(
                sortableTourStops.map(stop =>
                    (stop.stopId ?? stop.id) === (editedStop.stopId ?? editedStop.id) ? editedStop : stop
                )
            );
        };

        const updateSelectedOrderIds = useCallback(
            (nextSelectedOrderIds: string[]) => {
                setValue('selectedOrderIds', nextSelectedOrderIds, { shouldValidate: true, shouldDirty: true });
            },
            [setValue]
        );

        const removeStopFromTour = useCallback(
            (stop: TourStop) => {
                const order = selectedOrderById.get(stop.orderId);
                if (!canRemoveStopFromTour(sortableTourStops, stop, order)) {
                    showOrderCanNotBeRemovedFormTourNotification(intl);
                    return;
                }
                updateSelectedOrderIds(selectedOrderIds.filter(selectedOrderId => selectedOrderId !== stop.orderId));
            },
            [intl, selectedOrderById, selectedOrderIds, sortableTourStops, updateSelectedOrderIds]
        );

        const handleSelectedOrderIdsChange = useCallback(
            (nextSelectedOrderIds: string[]) => {
                const removedOrderIds = selectedOrderIds.filter(orderId => !nextSelectedOrderIds.includes(orderId));
                const removedStops = sortableTourStops.filter(stop => removedOrderIds.includes(stop.orderId));
                const hasBlockedRemovedStop = removedStops.some(
                    stop => !canRemoveStopFromTour(sortableTourStops, stop, selectedOrderById.get(stop.orderId))
                );
                if (hasBlockedRemovedStop) {
                    showOrderCanNotBeRemovedFormTourNotification(intl);
                    setValue('selectedOrderIds', selectedOrderIds, { shouldValidate: true });
                    return;
                }
                updateSelectedOrderIds(nextSelectedOrderIds);
            },
            [intl, selectedOrderById, selectedOrderIds, setValue, sortableTourStops, updateSelectedOrderIds]
        );

        useEffect(() => {
            if (!isOpen) {
                setPendingTourPayload(undefined);
                return;
            }

            if (!isDirty) {
                reset(createDefaultValues(orders, tourToEdit));
            }
        }, [isDirty, isOpen, orders, reset, tourToEdit]);

        useEffect(() => {
            if (!isOpen || !tourToEdit) {
                setNotificationsEnabled(true);
                return;
            }

            if (existingSubscriptions) {
                setNotificationsEnabled(existingSubscriptions.items.length > 0);
            }
        }, [existingSubscriptions, isOpen, tourToEdit]);

        const createSubscriptions = (tourId: string) => createSubscription(createTourSubscriptions(tourId)).unwrap();

        const deleteSubscriptions = async () => {
            const subscriptionsToDelete = existingSubscriptions?.items ?? [];
            const results = await Promise.allSettled(
                subscriptionsToDelete.map(subscription => deleteSubscription(subscription).unwrap())
            );
            const failedResult = results.find(result => result.status === 'rejected');
            if (failedResult?.status === 'rejected') {
                throw failedResult.reason;
            }
        };

        const syncSubscriptionsWithoutFailingTourSave = async (syncSubscriptions: () => Promise<unknown>) => {
            try {
                await syncSubscriptions();
            } catch (error) {
                console.error('Failed to synchronize tour subscriptions', error);
            }
        };

        const submitEditTour = async (payload: TourFormValues, tour: TourDetails) => {
            const updatedTour = await updateTour({
                id: tour.id,
                tour: ToBackendDTO(payload, sortableTourStops, tour.trailer.id),
            }).unwrap();
            const hasExistingSubscriptions = (existingSubscriptions?.items.length ?? 0) > 0;
            await syncSubscriptionsWithoutFailingTourSave(async () => {
                if (notificationsEnabled && !hasExistingSubscriptions) {
                    await createSubscriptions(updatedTour.id);
                } else if (!notificationsEnabled) {
                    await deleteSubscriptions();
                }
            });
            Notification.success(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.edit.success',
                    defaultMessage: 'Tour successfully edited!',
                })
            );
            close();
            navigate(`${routes.TOURS}/${updatedTour.id}`);
        };

        const submitCreateTour = async (payload: TourFormValues) => {
            const createdTour = await createTour(ToBackendDTO(payload, sortableTourStops)).unwrap();
            if (notificationsEnabled) {
                await syncSubscriptionsWithoutFailingTourSave(() => createSubscriptions(createdTour.id));
            }
            Notification.success(
                intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.create.success',

                    defaultMessage: 'Tour successfully created!',
                })
            );
            close();
            navigate(`${routes.TOURS}/${createdTour.id}`);
        };

        const submitTourPayload = async (payload: TourFormValues) => {
            try {
                if (tourToEdit) {
                    await submitEditTour(payload, tourToEdit);
                    return;
                }

                await submitCreateTour(payload);
            } catch {
                Notification.error(
                    intl.formatMessage({
                        id: isEditMode
                            ? 'intl-msg:ordercom-cloud-frontend.tours.edit.error'
                            : 'intl-msg:ordercom-cloud-frontend.tours.create.error',
                    })
                );
            }
        };

        const submitTour = throttle(
            handleSubmit(async payload => {
                if (hasExceededTourCapacity(sortableTourStops, selectedOrders, capacityLimits)) {
                    setPendingTourPayload(payload);
                    return;
                }

                await submitTourPayload(payload);
            }),
            500,
            { leading: true }
        );

        const handleClose = useCallback(() => {
            reset(createDefaultValues([]));
            setStopToEdit(undefined);
            setPendingTourPayload(undefined);
            close();
        }, [close, reset]);

        const confirmTourSave = async () => {
            const payload = pendingTourPayload;
            setPendingTourPayload(undefined);
            if (payload) {
                await submitTourPayload(payload);
            }
        };

        return (
            <Dialog
                show={isOpen}
                title={dialogTitle}
                body={
                    <Fade>
                        <TourForm
                            control={control}
                            isTourStarted={isTourStarted}
                            driverOptions={driverOptions}
                            vehicleOptions={vehicleOptions}
                            orderOptions={orderOptions}
                            onSelectedOrderIdsChange={handleSelectedOrderIdsChange}
                            showOrderSelection={!useNewTourDialog}
                            selectedOrders={selectedOrders}
                            showVehicleSelection={!useNewTourDialog}
                            showCapacity={!useNewTourDialog}
                        />
                        {!useNewTourDialog && selectedOrders.length === 0 && (
                            <EmptyState
                                condensed
                                outerClassName='border-none margin-y-20'
                                headline={intl.formatMessage({
                                    id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.no-orders-selected',
                                    defaultMessage: 'No orders selected',
                                })}
                            />
                        )}
                        {useNewTourDialog ? (
                            <div
                                className='display-flex flex-column flex-row-lg gap-20 margin-top-20 align-items-stretch overflow-y-auto'
                                style={{ height: 'calc(100vh - 400px)', minHeight: 300 }}
                                data-testid='create-tour-content-grid'
                            >
                                <div className='width-20pct-lg flex-shrink-0 height-100pct min-height-300'>
                                    <OpenOrdersList
                                        isOpen={isOpen}
                                        isLoading={isLoadingOrders}
                                        isError={isOrderError}
                                        onRetry={onRetryOrders}
                                        orders={selectableOrders}
                                        selectedOrderIds={selectedOrderIds}
                                        onSelectedOrderIdsChange={handleSelectedOrderIdsChange}
                                    />
                                </div>
                                <section
                                    className='flex-1-1-0 min-width-0 height-100pct min-height-300 display-flex flex-column'
                                    aria-label={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tour.stops',
                                        defaultMessage: 'Tour stops',
                                    })}
                                >
                                    <h4 className='margin-0 margin-bottom-10'>
                                        {intl.formatMessage({
                                            id: 'intl-msg:ordercom-cloud-frontend.tour.stops',
                                            defaultMessage: 'Tour stops',
                                        })}
                                    </h4>
                                    {sortableTourStops.length === 0 ? (
                                        <TourStopsEmptyState />
                                    ) : (
                                        <SortableTourList
                                            sortableTourStops={sortableTourStops}
                                            setSortableTourStops={setSortableTourStops}
                                            orders={selectedOrders}
                                            misplacedStopIds={misplacedStopIds}
                                            onRemoveStop={removeStopFromTour}
                                            editableStopIds={editableStopIds}
                                            onEditStop={setStopToEdit}
                                            showSequenceMarkers
                                            scrollable
                                            capacity={capacity}
                                        />
                                    )}
                                </section>
                                <div className='width-33pct-lg min-width-0 flex-shrink-0 height-100pct min-height-300 overflow-y-auto display-flex flex-column gap-20'>
                                    <TourStopsMap stops={sortableTourStops} height={300} />
                                    <div
                                        className={`form-group margin-0 min-width-0 min-height-0 flex-1-1 display-flex flex-column ${errors.vehicle ? 'has-error has-feedback' : ''}`}
                                    >
                                        <VehicleAvailabilityTimeline
                                            stops={sortableTourStops}
                                            selectedVehicleId={selectedVehicleId}
                                            onSelectVehicle={vehicleId =>
                                                setValue('vehicle', vehicleId, {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                })
                                            }
                                            excludedTourId={tourToEdit?.id}
                                            disabled={isTourStarted}
                                            skip={!isOpen}
                                        />
                                        <ValidationViolation violations={errors.vehicle?.message} />
                                    </div>
                                    {showLoadMetrics && (
                                        <TourCapacityForm
                                            control={control}
                                            orders={selectedOrders}
                                            compact
                                            exceededDimensions={exceededCapacityDimensions}
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <SortableTourList
                                sortableTourStops={sortableTourStops}
                                setSortableTourStops={setSortableTourStops}
                                orders={selectedOrders}
                                misplacedStopIds={misplacedStopIds}
                                onRemoveStop={removeStopFromTour}
                                editableStopIds={editableStopIds}
                                onEditStop={setStopToEdit}
                                capacity={capacity}
                            />
                        )}
                        {stopToEdit && (
                            <EditStopDialog
                                isOpen={!!stopToEdit}
                                stop={mapStopTypeToStop(stopToEdit)}
                                validateStop={updatedStop =>
                                    validateEditedStopOrder(updatedStop, stopToEdit, sortableTourStops)
                                }
                                onClose={() => setStopToEdit(undefined)}
                                onSubmit={(stopUpdate, updatedStop) => {
                                    updateStopInTourDialog({
                                        ...stopToEdit,
                                        name: updatedStop.name,
                                        location: updatedStop.location,
                                        start: updatedStop.startTarget,
                                        end: updatedStop.endTarget,
                                        latitude: updatedStop.latitude,
                                        longitude: updatedStop.longitude,
                                        comment: updatedStop.comment,
                                        stopUpdate,
                                    });
                                }}
                            />
                        )}
                        <CapacityExceededConfirmationDialog
                            mode={isEditMode ? 'edit' : 'create'}
                            show={pendingTourPayload !== undefined}
                            onEdit={() => setPendingTourPayload(undefined)}
                            onConfirm={confirmTourSave}
                        />
                    </Fade>
                }
                footer={
                    <DialogFooter
                        notificationsEnabled={notificationsEnabled}
                        setNotificationsEnabled={setNotificationsEnabled}
                        onSubmitTour={submitTour}
                        isMisplaced={isMisplaced || selectedOrders.length === 0}
                        isLoading={isLoading}
                        submitLabel={submitLabel}
                        submitTrackingKey={submitTrackingKey}
                    />
                }
                onClose={handleClose}
                bodyClassName='padding-20'
                bsSize={useNewTourDialog ? 'xl' : 'md'}
                showCloseButton={false}
                headerButtons={
                    <Button
                        bsStyle={Button.MUTED}
                        iconOnly
                        iconName='rioglyph-remove'
                        autoTrackingKey={`${dialogTitleId}.close`}
                        className='modal-header-close close'
                        onClick={handleClose}
                        aria-label='dialog close button'
                    />
                }
            />
        );
    },
    (prevProps, nextProps) =>
        prevProps.isOpen === nextProps.isOpen &&
        prevProps.tourToEdit?.id === nextProps.tourToEdit?.id &&
        prevProps.orders === nextProps.orders &&
        prevProps.availableOrders === nextProps.availableOrders &&
        prevProps.isLoadingOrders === nextProps.isLoadingOrders &&
        prevProps.isOrderError === nextProps.isOrderError &&
        prevProps.onRetryOrders === nextProps.onRetryOrders
);
